import { Button, ControlledTextarea, icon } from "@hm/fundamental"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

interface CommentEditFormProps {
	content: string
	onSave: (content: string) => Promise<void>
	onCancel: () => void
}

export const CommentEditForm: React.FC<CommentEditFormProps> = ({
	content,
	onSave,
	onCancel,
}) => {
	const { t } = useTranslation("shared", {
		keyPrefix: "commentBox.editActions",
	})

	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const [value, setValue] = useState(content)
	const [isSaving, setIsSaving] = useState(false)

	useEffect(() => {
		textareaRef.current?.focus()
		setValue(content)
	}, [content])

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault()
		const trimmed = value.trim()
		if (!trimmed) {
			return
		}

		setIsSaving(true)
		try {
			await onSave(trimmed)
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<form data-cy="comment-edit-form" onSubmit={handleSubmit}>
			<ControlledTextarea
				name="content"
				label=""
				ref={textareaRef}
				isDisabled={isSaving}
				value={value}
				onChange={event => setValue(event.target.value)}
			/>
			<div className="flex justify-end gap-x-3 mt-4px">
				<Button
					data-cy="comment-edit-cancel"
					variant="secondary"
					type="button"
					icon={icon.mdiClose}
					onClick={() => {
						setValue(content)
						onCancel()
					}}
					disabled={isSaving}
				>
					{t("cancel")}
				</Button>
				<Button
					data-cy="comment-edit-done"
					variant="primary"
					type="submit"
					icon={icon.mdiCheck}
					disabled={isSaving}
				>
					{t("done")}
				</Button>
			</div>
		</form>
	)
}
